import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';

import { getTimelineItems } from '#client/components/OrderTracker/utils';
import { ENABLE_ORDER_TRACKER_RA_IR } from '#client/constants/experimentsFlags';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';

/**
 * Hook to determine if the carousel should be shown on the home page.
 * The carousel is only shown when:
 * - OrderTracker has no visible timeline items AND
 * - User does not have banking entitlement
 * - Data is fully loaded (not during loading state)
 *
 * @returns {{ loading: boolean, shouldShow: boolean }}
 */
function useShowCarousel() {
  const { entitlements, loading: businessEntityLoading } = useBusinessEntity();
  const { loading: orderTrackerLoading, orderTasksWithStatus } = useOrderTrackerStatus();
  const enableRaIr = useExperimentFlag(ENABLE_ORDER_TRACKER_RA_IR, false);

  // Don't show during loading
  const loading = businessEntityLoading || orderTrackerLoading;
  if (loading) {
    return { loading: true, shouldShow: false };
  }

  // If we don't have entitlements data, don't show carousel
  if (!entitlements) {
    return {
      loading: false,
      shouldShow: false,
    };
  }

  const timelineItems = getTimelineItems(orderTasksWithStatus, { enableRaIr });
  const hasOrderTrackerItems = timelineItems.length > 0;
  const hasBankingEntitlement = entitlements.has('banking');

  // Show only when both are false
  return {
    loading: false,
    shouldShow: !hasOrderTrackerItems && !hasBankingEntitlement,
  };
}

export { useShowCarousel };
